import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CheckIcon, PauseIcon, RefreshCcwIcon, TriangleAlertIcon } from '@air/design-icons';
import {
  type ContentCollectionClipWithUpload,
  contentCollectionFormIsInvalidSelector,
  contentCollectionRequiredFieldsSelector,
} from '@air/feature-content-collection/redux';
import { Chip, type ChipProps } from '@air/primitive-chip';
import { useBreakpointsContext } from '@air/provider-media-query';
import { UploadStatus } from '@air/redux-uploader';
import { cn } from '@air/utils-tailwind';

import { type AirState } from '~/store/types';

export type ContentCollectionGalleryAssetProps = {
  clip: ContentCollectionClipWithUpload;
  isHovering: boolean;
};

export const ContentCollectionGalleryAssetStatus = memo(({ clip, isHovering }: ContentCollectionGalleryAssetProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();

  const requiredFields = useSelector(contentCollectionRequiredFieldsSelector);
  const isInvalid = useSelector((st: AirState) => contentCollectionFormIsInvalidSelector(st, [clip.id]));

  const showLabel = !isAboveMediumScreen || isHovering;

  const chipProps = useMemo<(ChipProps & { testId: string }) | null>(() => {
    switch (clip.upload.status) {
      case UploadStatus.preparing:
      case UploadStatus.queued:
      case UploadStatus.uploading:
        return {
          testId: 'CONTENT_COLLECTION_GALLERY_ASSET_STATUS-processing',
          color: 'light-yellow',
          children: (
            <>
              <RefreshCcwIcon size="xs" className="animate-spin" />
              <span className="font-medium text-12 normal-case">Processing</span>
            </>
          ),
        };

      case UploadStatus.paused:
        return {
          testId: 'CONTENT_COLLECTION_GALLERY_ASSET_STATUS-paused',
          color: 'light-blue',
          children: (
            <>
              <PauseIcon size="xs" />
              <span className={cn('font-medium text-12 normal-case', !showLabel && 'hidden')}>Paused</span>
            </>
          ),
        };

      case UploadStatus.completed:
        if (isInvalid) {
          return {
            testId: 'CONTENT_COLLECTION_GALLERY_ASSET_STATUS-metadata-missing',
            color: 'light-red',
            children: (
              <>
                <TriangleAlertIcon size="xs" />
                <span className={cn('font-medium text-12 normal-case', !showLabel && 'hidden')}>Metadata missing</span>
              </>
            ),
          };
        }

        if (!requiredFields.length) {
          return null;
        }

        return {
          testId: 'CONTENT_COLLECTION_GALLERY_ASSET_STATUS-ready',
          color: 'light-green',
          children: (
            <>
              <CheckIcon size="xs" />
              <span className={cn('font-medium text-12 normal-case', !showLabel && 'hidden')}>Ready to submit</span>
            </>
          ),
        };

      default:
        return {
          testId: 'CONTENT_COLLECTION_GALLERY_ASSET_STATUS-error',
          color: 'red',
          children: (
            <>
              <TriangleAlertIcon size="xs" />
              <span className="font-medium text-12 normal-case">Upload error</span>
            </>
          ),
        };
    }
  }, [clip.upload.status, isInvalid, requiredFields, showLabel]);

  if (!chipProps) {
    return null;
  }

  const { testId, ...restOfChipProps } = chipProps;

  return <Chip {...restOfChipProps} data-testid={testId} data-filename={clip.upload.file.name} />;
});

ContentCollectionGalleryAssetStatus.displayName = 'ContentCollectionGalleryAssetStatus';
